import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Container,
  TextField,
  Button,
  CircularProgress,
  Avatar,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useIntl } from "react-intl";
import {
  useHistory,
  useLocation,
  Link as RouterLink,
  Prompt,
} from "react-router-dom";

import Title from "../../ui/Title";
import FormSection from "../../ui/forms/input/FormSection";

import { useBmapi } from "../../utils/bmapi-context";
import { getErrorMessageString } from "../../utils/errors";
import { MANAGER_ROUTES } from "../../utils/constants";
import {
  account,
  confirm as confirmMsgs,
  common,
  professional,
} from "../../messages";

import FieldMonolang from "../../ui/forms/input/FieldMonolang";
import { ArrowBack, ArrowForward } from "@material-ui/icons";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { validateEmail } from "../../utils/utils";

const useStyles = makeStyles(() => ({
  submitButton: {
    marginTop: 16,
    height: 48,
    borderRadius: 24,
    fontWeight: "bold",
    backgroundColor: "#8B1E2D",
    color: "#fff",
    position: "relative",
    "&:hover": {
      backgroundColor: "#741826",
    },
  },
  bottomLinks: {
    marginTop: 16,
    display: "flex",
    justifyContent: "space-between",
  },
  imageBox: {
    display: "flex",
    alignItems: "center",
    gap: 16,
  },
}));

export default function ManageProfessional() {
  const classes = useStyles();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const { bmapi, notifyError, notifySuccess } = useBmapi();

  const searchParams = new URLSearchParams(location.search);
  const id = searchParams.get("id");
  const cloneId = searchParams.get("cloneId");

  const [values, setValues] = useState({
    first_name: "",
    last_name: "",
    email: "",
    short_descr: "",
    long_descr: "",
    long_descr_format: "text",
    categories: [],
    image_url: "",
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [removeImageFlag, setRemoveImageFlag] = useState(false); // ✅ NEW

  const [categoriesOptions, setCategoriesOptions] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);

  const withBusy = useCallback(async (fn) => {
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  }, []);

  const Overlay = () =>
    busy ? (
      <Box
        position="fixed"
        top={0}
        left={0}
        right={0}
        bottom={0}
        display="flex"
        alignItems="center"
        justifyContent="center"
        bgcolor="rgba(255,255,255,0.6)"
        zIndex={9999}
      >
        <CircularProgress size={60} />
      </Box>
    ) : null;

  // LOAD
  useEffect(() => {
    const load = async () => {
      if (!bmapi) return;

      if (id) {
        const resp = await bmapi.getProfessionalAdmin(id);
        setValues((v) => ({ ...v, ...(resp || {}) }));
        setImagePreview(resp?.image_url || null);
      } else if (cloneId) {
        const resp = await bmapi.getProfessionalAdmin(cloneId);
        if (resp) {
          const cloned = { ...resp };
          delete cloned.id;
          delete cloned.created_at;
          delete cloned.updated_at;
          setValues((v) => ({ ...v, ...cloned }));
          setImagePreview(cloned?.image_url || null);
        }
      }
    };

    load().catch((e) => notifyError(getErrorMessageString(e, intl)));
  }, [bmapi, id, cloneId, notifyError, intl]);

  // LOAD categories
  useEffect(() => {
    if (!bmapi) return;

    bmapi
      .getGenListCategoriesProfessional()
      .then((resp) => {
        const options =
          resp?.items?.map((i) => ({
            value: i.id,
            label: i.str_value,
          })) || [];
        setCategoriesOptions(options);
      })
      .catch((e) => notifyError(getErrorMessageString(e, intl)));
  }, [bmapi, notifyError, intl]);

  const handleChange = (field) => (e, value) => {
    setDirty(true);

    let newValue;

    if (e && e.target) newValue = e.target.value;
    else if (typeof e === "string") newValue = e;
    else if (value !== undefined) newValue = value;
    else newValue = "";

    setValues((v) => ({ ...v, [field]: newValue }));
  };

  // IMAGE UPLOAD
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setDirty(true);
    setImageFile(file);
    setRemoveImageFlag(false); // ✅ reset delete flag

    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setDirty(true);
    setImageFile(null);
    setImagePreview(null);
    setRemoveImageFlag(true); // ✅ mark for deletion

    setValues((v) => ({ ...v, image_url: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (values.email && !validateEmail(values.email)) {
      notifyError("Email non valida");
      return;
    }

    await withBusy(async () => {
      try {
        let saved = null;

        // ✅ QUERY
        const query = removeImageFlag ? { remove_image: true } : {};

        if (id) {
          await bmapi.updateProfessional(id, values, query);
          saved = { id };
        } else {
          saved = await bmapi.createProfessional(values);
        }

        if (imageFile && saved?.id) {
          await bmapi.uploadProfessionalImage(saved.id, imageFile);
        }

        notifySuccess(intl.formatMessage(account.saveConfirm));
        setDirty(false);

        history.goBack();
      } catch (e) {
        notifyError(getErrorMessageString(e, intl));
      }
    });
  };

  const isEdit = !!id;

  return (
    <Container maxWidth="sm">
      <Overlay />

      <Prompt
        when={dirty && !busy}
        message={intl.formatMessage(confirmMsgs.exitPrompt)}
      />

      <Title>
        {intl.formatMessage(isEdit ? professional.edit : professional.create)}
      </Title>

      <form onSubmit={handleSubmit}>
        <FormSection>
          <TextField
            label="Nome"
            value={values.first_name || ""}
            onChange={handleChange("first_name")}
            fullWidth
            margin="normal"
            required
            disabled={busy}
          />

          <TextField
            label="Cognome"
            value={values.last_name || ""}
            onChange={handleChange("last_name")}
            fullWidth
            margin="normal"
            required
            disabled={busy}
          />

          <TextField
            label="Email"
            value={values.email || ""}
            onChange={handleChange("email")}
            fullWidth
            margin="normal"
            type="text"
            disabled={busy}
          />

          {/* IMAGE */}
          <Box className={classes.imageBox} mt={2}>
            <Avatar
              src={imagePreview || ""}
              style={{ width: 80, height: 80 }}
            />
            <Box>
              <Button component="label" variant="contained" disabled={busy}>
                Upload
                <input
                  hidden
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                />
              </Button>
              <Box mt={1}>
                <Button onClick={removeImage} disabled={!imagePreview || busy}>
                  Rimuovi
                </Button>
              </Box>
            </Box>
          </Box>

          <TextField
            label="Descrizione breve"
            value={values.short_descr || ""}
            onChange={handleChange("short_descr")}
            fullWidth
            margin="normal"
            multiline
            rows={2}
            disabled={busy}
          />

          <FieldMonolang
            name="long_descr"
            label="Descrizione lunga"
            value={values.long_descr}
            onChange={(val) => {
              setDirty(true);
              setValues((v) => ({
                ...v,
                long_descr:
                  typeof val === "string" ? val : val?.target?.value || "",
              }));
            }}
            hasFormat={true}
            formatValue={values.long_descr_format}
            onFormatChange={(val) => {
              setDirty(true);
              setValues((v) => ({
                ...v,
                long_descr_format:
                  typeof val === "string" ? val : val?.target?.value || "",
              }));
            }}
          />

          <Autocomplete
            multiple
            options={categoriesOptions}
            getOptionLabel={(o) => o.label}
            value={categoriesOptions.filter((opt) =>
              (values.categories || []).includes(opt.value)
            )}
            onChange={(e, newValue) => {
              setDirty(true);
              setValues((v) => ({
                ...v,
                categories: newValue.map((o) => o.value),
              }));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Categorie"
                margin="normal"
                disabled={busy}
              />
            )}
          />
        </FormSection>

        <Box position="relative">
          <Button
            type="submit"
            fullWidth
            className={classes.submitButton}
            disabled={busy}
          >
            {intl.formatMessage(isEdit ? common.save : common.create)}

            {busy && (
              <CircularProgress
                size={20}
                style={{
                  position: "absolute",
                  right: 16,
                  top: "50%",
                  marginTop: -10,
                  color: "#fff",
                }}
              />
            )}
          </Button>
        </Box>

        <Box className={classes.bottomLinks}>
          <Button
            component={RouterLink}
            to={MANAGER_ROUTES.PROFESSIONALS}
            startIcon={<ArrowBack />}
            disabled={busy}
          >
            {intl.formatMessage(common.back)}
          </Button>

          <Button
            component={RouterLink}
            to="/"
            startIcon={<ArrowForward />}
            disabled={busy}
          >
            {intl.formatMessage(common.backHome)}
          </Button>
        </Box>
      </form>
    </Container>
  );
}
